/**
 * Mobile app hand-off for the CMS call buttons.
 *
 * On a phone, a Call click should ring through the Artium mobile app rather
 * than the browser softphone — the app owns the microphone, survives the
 * screen locking, and is already signed in. There is no way to ask a browser
 * "is this app installed", so the hand-off is the standard custom-scheme
 * probe: navigate to `artiumtel://dial/<E.164>` and watch whether the page
 * gets backgrounded. Backgrounded means the OS handed the URL to the app;
 * still visible after ~3s means nothing claimed the scheme, and the
 * existing web flow (dock + `artium:dial`) runs untouched.
 *
 * The navigation MUST happen here, in the CMS page's own context and
 * synchronously inside the click handler: mobile browsers block custom-scheme
 * navigation from a cross-origin iframe (which is what the softphone is), and
 * some block it outside a user gesture too.
 *
 * Contract confirmed with the mobile team: the number is the ONLY parameter
 * the app accepts — no source, no sourceRecordId. If the app is installed but
 * signed out it opens its login and applies the number after, so an opened
 * app is a success either way and the CMS must not also dial.
 */

/**
 * Per-environment kill switch, on in UAT only. Telephony itself is live in
 * every environment, so the deep link needs its own flag to stay UAT-scoped;
 * unset (beta/prod/local) means the mobile hand-off never runs and calling
 * behaves exactly as it does today.
 */
export const MOBILE_DEEPLINK_ENABLED =
  process.env.REACT_APP_MOBILE_DEEPLINK_ENABLED === 'true';

/** Custom URL scheme registered by the Artium mobile app. */
export const DIAL_SCHEME = 'artiumtel://dial/';

/**
 * Opening the app with no number, for the floating call bubble — the bubble
 * means "give me my phone", not "call this person".
 *
 * UNCONFIRMED: the mobile team confirmed `artiumtel://dial/<E.164>` and
 * nothing else, so the no-number form is this side's assumption. The bare
 * scheme is chosen for its failure mode rather than its odds: if the app's
 * intent filter turns out to require the `dial` host, nothing claims this URL
 * and the bubble just opens the dock as it does today. The alternative,
 * `artiumtel://dial/`, matches a host-scoped filter too but hands the app an
 * empty number to parse — if that lands on an error screen we would have
 * suppressed the dock for it. Worth one question to the mobile team; if they
 * confirm the host is required, this constant is the only line to change.
 */
export const APP_OPEN_LINK = 'artiumtel://';

/**
 * How long to wait for the OS to background us before deciding no app
 * claimed the scheme.
 *
 * Was 1500 — too short against the iOS "Open in Artium?" confirmation
 * sheet, which needs an actual human tap and routinely takes longer than
 * that to answer. `visibilitychange`/`pagehide` correctly don't fire just
 * because the sheet is showing (only once the user taps Open and the OS
 * actually switches apps), so a short timeout elapsed first, fired
 * `onFallback()`, and rang the call through the web softphone before the
 * user had even responded to the sheet — the app then opened moments
 * later onto a call already in flight, landing on a stuck "Connecting".
 * 3000ms gives real tap time room without making the no-app case feel
 * like a hang.
 */
const FALLBACK_MS = 3000;

/**
 * The same default region the telephony platform normalises with
 * (`numberPool.normalize(..., agent.region ?? 'IN')`), so a bare national
 * number deep-links to the same destination the web call would have reached.
 */
const DEFAULT_COUNTRY_CODE = '91';

/**
 * Best-effort E.164, deliberately conservative: anything it cannot be sure
 * about returns null and the caller falls back to the web flow rather than
 * handing the app a number it would dial wrongly.
 *
 * Hand-rolled rather than pulling in libphonenumber — the CMS has no
 * phone-number dependency today, and the only job here is to format a number
 * the platform re-validates anyway.
 */
export function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  // Number cells carry separators and the odd `tel:` paste; the country code
  // arrives as a separate field ('+91', '91' or empty) concatenated in front.
  const trimmed = String(raw).trim().replace(/^tel:/i, '');
  const hadPlus = trimmed.startsWith('+');
  let digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;

  if (!hadPlus) {
    if (digits.startsWith('00')) {
      digits = digits.slice(2); // 00-prefixed international dialling
    } else if (digits.length === 10) {
      digits = DEFAULT_COUNTRY_CODE + digits; // bare Indian mobile
    } else if (digits.length === 11 && digits.startsWith('0')) {
      digits = DEFAULT_COUNTRY_CODE + digits.slice(1); // 0-trunk national form
    }
  }

  // ITU-T E.164 allows at most 15 digits; below 8 it is not a reachable
  // international number and is far more likely to be a mangled cell.
  if (digits.length < 8 || digits.length > 15) return null;
  return `+${digits}`;
}

/** `artiumtel://dial/+919876543210` — the number and nothing else. */
export const buildDialDeepLink = (e164: string): string => `${DIAL_SCHEME}${e164}`;

/** True on phones/tablets, where the app can exist. Desktop never probes. */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const hints = (navigator as unknown as { userAgentData?: { mobile?: boolean } })
    .userAgentData;
  if (hints && typeof hints.mobile === 'boolean') return hints.mobile;
  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ reports a desktop Mac UA; touch points are what give it away.
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * One probe at a time, module-scoped rather than per-component: the Call
 * buttons are spread across independently mounted rows, so a ref inside any
 * one of them would not stop a second row starting a rival timer. A
 * double-tap on the same button lands here too, since both clicks fire before
 * the first probe resolves, as does tapping the bubble mid-dial.
 */
let probeInFlight = false;

/**
 * Navigate to `url` and watch whether the OS takes us out of the browser.
 * Shared by every hand-off; the only thing that varies is the URL.
 */
function probeDeepLink(url: string, onFallback: () => void): boolean {
  // Swallow the extra click rather than acting twice: a probe is already
  // running and will either open the app or fall back on its own.
  if (probeInFlight) return true;
  probeInFlight = true;

  let settled = false;
  let timer = 0;

  const finish = (): void => {
    settled = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onBackgrounded);
    window.removeEventListener('pagehide', onBackgrounded);
    probeInFlight = false;
  };

  /**
   * The page going away is our only evidence the app took the URL.
   * `visibilitychange` covers the app switch on both platforms; `pagehide`
   * covers browsers that tear the page down instead. Note the visible-again
   * edge: returning from the app fires visibilitychange a second time, so the
   * handler ignores anything that is not actually a hide.
   *
   * `blur` is deliberately NOT treated as success — iOS fires it for the
   * "Open in Artium?" confirmation sheet, so a user tapping Cancel there
   * would lose their call entirely.
   */
  function onBackgrounded(): void {
    if (settled) return;
    if (!document.hidden && document.visibilityState !== 'hidden') return;
    finish(); // app opened — no dock, no artium:dial
  }

  // Registered before navigating: on a fast hand-off the page can be hidden
  // before the assignment below returns.
  document.addEventListener('visibilitychange', onBackgrounded);
  window.addEventListener('pagehide', onBackgrounded);

  timer = window.setTimeout(() => {
    if (settled) return;
    finish();
    // Last look in case the hide events never fired but we are backgrounded
    // anyway — dialling into a hidden tab would strand the call.
    if (document.hidden) return;
    onFallback();
  }, FALLBACK_MS);

  // Synchronous, top-level navigation from the CMS page itself. A scheme no
  // app claims is a no-op in Chrome/Android and shows a dismissible dialog in
  // iOS Safari; either way the page stays visible and the timer fires.
  window.location.href = url;

  return true;
}

/**
 * Try to hand this call to the mobile app.
 *
 * Returns true when a probe was started — the caller must then do nothing
 * else: either the app opened (web flow ends) or `onFallback` runs in ~1.5s.
 * Returns false when the hand-off does not apply (desktop, flag off, unusable
 * number), leaving the caller to dial on the web immediately.
 */
export function tryMobileAppDial(rawNumber: string, onFallback: () => void): boolean {
  if (!MOBILE_DEEPLINK_ENABLED || !isMobileBrowser()) return false;

  const e164 = toE164(rawNumber);
  if (!e164) return false;

  return probeDeepLink(buildDialDeepLink(e164), onFallback);
}

/**
 * Try to open the mobile app with no number — the floating bubble's job,
 * which is "give me my phone" rather than "call this person".
 *
 * Same contract as `tryMobileAppDial`: true means the click is spoken for,
 * false means open the dock right now.
 */
export function tryMobileAppOpen(onFallback: () => void): boolean {
  if (!MOBILE_DEEPLINK_ENABLED || !isMobileBrowser()) return false;

  return probeDeepLink(APP_OPEN_LINK, onFallback);
}
